import { useLanguageDirection } from "@hooks/useLanguageDirection";
import React, { useCallback, useState } from "react";

type ScrollPositionOptions = {
  scrollOffset?: number;
  defaultAtTop?: boolean;
  defaultAtBottom?: boolean;
  defaultAtStart?: boolean;
  defaultAtEnd?: boolean;
  direction?: "vertical" | "horizontal";
};

/**
 * Hook to detect the scroll position of a container
 * @param containerRef
 * @param options
 * @returns isScrollbarAtBottom, isScrollbarAtTop, checkPosition()
 */
export const useScrollPositionDetector = <TRefObject extends HTMLElement>(
  containerRef: React.RefObject<TRefObject>,
  options?: ScrollPositionOptions,
) => {
  const {
    scrollOffset = 2,
    defaultAtTop = true,
    defaultAtBottom = true,
    defaultAtStart = true,
    defaultAtEnd = true,
    direction = "vertical",
  } = options ?? {};

  const languageDirection = useLanguageDirection();
  const isRTL = languageDirection === "rtl";

  const [isScrollbarAtBottom, setIsScrollbarAtBottom] = useState(defaultAtBottom);
  const [isScrollbarAtTop, setIsScrollbarAtTop] = useState(defaultAtTop);
  const [isScrollbarAtStart, setIsScrollbarAtStart] = useState(defaultAtStart);
  const [isScrollbarAtEnd, setIsScrollbarAtEnd] = useState(defaultAtEnd);

  const checkPosition = useCallback(() => {
    if (containerRef.current) {
      // Vertical scroll
      if (direction === "vertical") {
        const element = containerRef.current;
        const isBottom = element.scrollHeight - element.clientHeight - element.scrollTop <= scrollOffset;
        const isTop = element.scrollTop <= scrollOffset;
        setIsScrollbarAtBottom(isBottom);
        setIsScrollbarAtTop(isTop);
      }

      // Horizontal scroll
      if (direction === "horizontal") {
        const element = containerRef.current;
        const scrollWidth = element.scrollWidth;
        const clientWidth = element.clientWidth;
        const scrollLeft = element.scrollLeft;
        const maxScroll = scrollWidth - clientWidth;

        let isStart: boolean;
        let isEnd: boolean;

        if (isRTL) {
          // In RTL:
          // - Chrome/Safari: scrollLeft ranges from 0 (start/visual right) to maxScroll (end/visual left)
          // - Firefox: scrollLeft ranges from 0 (start/visual right) to -maxScroll (end/visual left)
          // At start (visual right): scrollLeft is close to 0
          // At end (visual left): scrollLeft is close to maxScroll (Chrome) or -maxScroll (Firefox)

          // Check if at start (visual right) - scrollLeft is close to 0
          isStart = Math.abs(scrollLeft) <= scrollOffset;

          // Check if at end (visual left) - scrollLeft is close to maxScroll or -maxScroll
          // For Chrome: scrollLeft >= maxScroll - scrollOffset
          // For Firefox: scrollLeft <= -maxScroll + scrollOffset
          // We can check both conditions: Math.abs(scrollLeft - maxScroll) <= scrollOffset won't work for Firefox
          // Better: check if distance from max scroll position is within offset
          const distanceFromEnd = Math.abs(Math.abs(scrollLeft) - maxScroll);
          isEnd = distanceFromEnd <= scrollOffset;
        } else {
          // In LTR: scrollLeft ranges from 0 (left/start) to maxScroll (right/end)
          isStart = scrollLeft <= scrollOffset;
          isEnd = maxScroll - scrollLeft <= scrollOffset;
        }

        setIsScrollbarAtEnd(isEnd);
        setIsScrollbarAtStart(isStart);
      }
    } else {
      setIsScrollbarAtTop(true);
      setIsScrollbarAtStart(true);
    }
  }, [containerRef, direction, scrollOffset, isRTL]);

  return { isScrollbarAtBottom, isScrollbarAtTop, checkPosition, isScrollbarAtStart, isScrollbarAtEnd };
};
